import { FunctionalComponent } from 'preact'
import { Copy, Check } from 'lucide-preact'
import { useState } from 'preact/hooks'
import { useToast } from '@/hooks/useToast'
import { useTheme } from '@/hooks/useTheme'

interface CopyButtonProps {
  /** The text to copy to clipboard */
  text: string
  /** Label shown in success toast message (e.g., "API Key", "Form ID") */
  label?: string
  /** Visual variant of the button */
  variant?: 'default' | 'icon-only' | 'ghost'
  /** Size of the button */
  size?: 'sm' | 'md'
  /** Additional CSS classes */
  className?: string
}

export const CopyButton: FunctionalComponent<CopyButtonProps> = ({
  text,
  label = 'Text',
  variant = 'icon-only',
  size = 'sm',
  className = ''
}) => {
  const { showSuccess, showError } = useToast()
  const { resolvedTheme } = useTheme()
  const [copied, setCopied] = useState(false)
  const isDark = resolvedTheme === 'dark'

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      showSuccess('Copied', `${label} copied to clipboard.`)
      
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      showError('Copy Failed', e instanceof Error ? e.message : 'Could not access clipboard')
    }
  }

  // Size classes
  const sizeClasses = size === 'sm' 
    ? 'text-xs px-2 py-1' 
    : 'text-sm px-3 py-1.5'

  // Variant classes with proper theme support
  const variantClasses = {
    'default': isDark 
      ? 'rounded border border-slate-600 bg-slate-800 hover:bg-slate-700 text-slate-200'
      : 'rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700',
    'icon-only': isDark
      ? 'p-1 rounded hover:bg-slate-700 text-slate-300'
      : 'p-1 rounded hover:bg-slate-200 text-slate-600',
    'ghost': isDark
      ? 'rounded hover:bg-slate-700 text-slate-300'
      : 'rounded hover:bg-slate-100 text-slate-700'
  }

  const baseClasses = 'inline-flex items-center gap-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500'

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`${baseClasses} ${variant === 'icon-only' ? '' : sizeClasses} ${variantClasses[variant]} ${className}`}
      title={`Copy ${label}`}
    >
      {copied ? (
        <>
          <Check className={`w-3.5 h-3.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
          {variant !== 'icon-only' && (
            <span className={isDark ? 'text-green-400' : 'text-green-600'}>Copied</span>
          )}
        </>
      ) : (
        <>
          <Copy className={`w-3.5 h-3.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          {variant !== 'icon-only' && <span>Copy</span>}
        </>
      )}
    </button>
  )
}
